import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import useArenaxRealtime from './hooks/useArenaxRealtime';
import ArenaxReactionDock from './ArenaxReactionDock';
import {
  MapPin, Clock, Users, Calendar, Crown, ArrowRight, MessageCircle,
  Send, Loader2, X, Flame, Radio, Sparkles, CheckCircle, AlertTriangle, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

const BADGE_STYLES = {
  filling_fast: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: Flame, pulse: true },
  live: { color: '#10b981', bg: 'rgba(16,185,129,0.15)', icon: Radio, pulse: true },
  need_players: { color: '#06b6d4', bg: 'rgba(6,182,212,0.15)', icon: Users, pulse: false },
  confirmed: { color: '#a855f7', bg: 'rgba(168,85,247,0.15)', icon: Sparkles, pulse: false },
  completed: { color: '#64748b', bg: 'rgba(100,116,139,0.15)', icon: Clock, pulse: false },
  cancelled: { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: Clock, pulse: false },
};

export default function ArenaxGameDetail() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [returning, setReturning] = useState(null);
  const [reputations, setReputations] = useState({}); // {player_id: rep}

  // Phase 2G PR 3 — subscribe to live join-count + watcher channel
  const rt = useArenaxRealtime({ channel: 'game', channelId: gameId, enabled: !!gameId });

  // Apply live progress updates to the local game state without re-fetching
  useEffect(() => {
    if (!rt.lastMessage || rt.lastMessage.type !== 'game_progress') return;
    setGame(prev => prev ? {
      ...prev,
      participants_count: rt.lastMessage.joined_count,
      spots_remaining: rt.lastMessage.spots_remaining,
    } : prev);
  }, [rt.lastMessage]);

  const reload = async () => {
    try {
      const r = await api.get(`/arenax/games/${gameId}`);
      setGame(r.data);
      // Phase 2C: fetch returning players for squad games
      if (r.data.squad_id) {
        try {
          const rp = await api.get(`/arenax/games/${gameId}/returning-players`);
          setReturning(rp.data);
        } catch {}
      }
      // Phase 2D: batch-fetch participant reputations (player-only endpoint;
      // skip silently when viewer is anonymous or not a player).
      try {
        const pids = (r.data.participants || []).map(p => p.player_id).filter(Boolean);
        if (pids.length > 0) {
          const rb = await api.post('/arenax/reputation/batch', { player_ids: pids });
          setReputations(rb.data.reputations || {});
        }
      } catch {}
    } catch {
      setError('Game not found');
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [gameId]);

  const isParticipant = game?.participants?.some(p => p.player_id === user?.id);
  const isHost = game?.created_by === user?.id;
  const badge = game?.badge || {};
  const style = BADGE_STYLES[badge.kind] || BADGE_STYLES.confirmed;

  const onJoin = async () => {
    if (!user) { navigate(`/arenax/auth/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    if (!user) { toast.error('Sign in as a player to join.'); return; }
    setJoining(true);
    try {
      await api.post(`/arenax/games/${gameId}/join`);
      toast.success("You're in! Squad chat unlocked.");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join');
    } finally { setJoining(false); }
  };

  const onLeave = async () => {
    try { await api.post(`/arenax/games/${gameId}/leave`); toast.success('Left the game'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not leave'); }
  };
  const onCancel = async () => {
    if (!window.confirm('Cancel this game? All players will be refunded.')) return;
    try { await api.post(`/arenax/games/${gameId}/cancel`); toast.success('Game cancelled. Refunds processed.'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not cancel'); }
  };

  if (error) return <ArenaxLayout><div className="text-center py-32"><div className="text-slate-300 mb-3">{error}</div><Link to="/arenax/games" className="text-cyan-300 text-sm">← Back to games</Link></div></ArenaxLayout>;
  if (!game) return <ArenaxLayout><div className="text-center text-slate-500 py-32">Loading game...</div></ArenaxLayout>;

  const Icon = style.icon;

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {/* Cover */}
        <div className="rounded-3xl overflow-hidden border border-cyan-500/15 mb-8" data-testid="arenax-game-detail-hero">
          <div className="aspect-[16/6] relative" style={{
            background: game.venue_cover ? `url(${game.venue_cover}) center/cover` : 'linear-gradient(135deg, #0e7490, #f59e0b)',
          }}>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md backdrop-blur-md border"
                  style={{ background: style.bg, borderColor: style.color + '50' }}>
                  {style.pulse && <span className="relative h-1.5 w-1.5"><span className="absolute inset-0 rounded-full" style={{ background: style.color }} /><span className="absolute inset-0 rounded-full animate-ping" style={{ background: style.color }} /></span>}
                  <Icon className="h-3 w-3" style={{ color: style.color }} />
                  <span className="text-[10px] font-bold tracking-wider" style={{ color: style.color }}>{badge.text}</span>
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-slate-300 font-bold">{game.skill_level}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">· {game.game_type.replace('_', ' ')}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-100 flex items-center gap-3">
                <span>{SPORT_ICONS[game.sport] || '🏟️'}</span>
                {game.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300 mt-2">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-cyan-400" />{game.venue_name} · {game.city}</span>
                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-amber-400" />{new Date(game.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-400" />{game.start_time}–{game.end_time}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Game info + Participants + Squad Chat */}
          <div className="lg:col-span-2 space-y-5">
            {/* Needs / description */}
            {game.needs_note && (
              <div className="rounded-2xl border border-amber-500/30 p-4 flex items-start gap-3" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08), var(--ax-card-bg-soft))' }}>
                <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-amber-300 font-bold mb-1">Host's note</div>
                  <div className="text-sm text-slate-200">"{game.needs_note}"</div>
                </div>
              </div>
            )}

            {/* Returning players social-proof badge — Phase 2C */}
            {returning && returning.returning_count > 0 && (
              <div className="rounded-2xl border border-emerald-500/30 p-4 flex items-start gap-3" data-testid="arenax-returning-players"
                style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.10), var(--ax-card-bg-soft))' }}>
                <div className="h-10 w-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                  <Users className="h-5 w-5 text-emerald-300" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-300 font-bold mb-1">FAMILIAR FACES</div>
                  <div className="text-sm text-slate-100 font-bold">
                    {returning.returning_count} {returning.returning_count === 1 ? 'player is' : 'players are'} returning from a previous game
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {returning.returning_players.slice(0, 4).map(p => p.player_name).join(' · ')}
                    {returning.returning_count > 4 && ` · +${returning.returning_count - 4} more`}
                  </div>
                </div>
              </div>
            )}

            {/* Participants */}
            <div className="rounded-2xl border border-slate-800 p-5" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }} data-testid="arenax-game-participants">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-cyan-300" />
                  <h3 className="text-sm font-bold text-slate-100">Squad</h3>
                  {/* Phase 2G PR 3 — Live watchers pill */}
                  {rt.connected && rt.watchers != null && rt.watchers > 0 && (
                    <span data-testid="arenax-live-watchers"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-emerald-300 border border-emerald-500/30 bg-emerald-500/10"
                      title={`${rt.watchers} player${rt.watchers !== 1 ? 's' : ''} watching live`}>
                      <Eye className="h-2.5 w-2.5" />
                      <span className="tabular-nums">{rt.watchers} live</span>
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 tabular-nums font-semibold transition-all">{game.participants_count}/{game.max_players}</span>
                  {user && (
                    <ArenaxReactionDock targetType="game" targetId={gameId} incomingReaction={rt.lastReaction} />
                  )}
                </div>
              </div>
              {/* Capacity bar */}
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden mb-4">
                <div className="h-full rounded-full transition-all" style={{
                  width: `${Math.min(100, (game.participants_count / game.max_players) * 100)}%`,
                  background: 'var(--ax-primary-gradient)',
                }} />
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {(game.participants || []).map(p => {
                  const rep = reputations[p.player_id];
                  const primaryBadge = rep?.badges?.[0];
                  return (
                  <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-900/40 border border-slate-800"
                    data-testid={`arenax-participant-row-${p.player_id}`}>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-slate-950"
                        style={{ background: p.role === 'host' ? 'linear-gradient(135deg, #f59e0b, #ec4899)' : 'linear-gradient(135deg, #06b6d4, #a855f7)' }}>
                        {(p.player_name || 'P')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-100 flex items-center gap-1.5">
                          {p.player_name}
                          {p.role === 'host' && <Crown className="h-3 w-3 text-amber-300" />}
                          {primaryBadge && (
                            <span data-testid={`arenax-participant-rep-${p.player_id}`}
                              title={primaryBadge.hint}
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider"
                              style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
                              {primaryBadge.label}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                          {p.role === 'host' ? 'host · paid the venue' : `joined · ${(p.entry_fee_paid || 0) > 0 ? `₹${p.entry_fee_paid}` : 'free'}`}
                          {rep?.attendance_rate != null && rep.games_played >= 3 && (
                            <span className="text-slate-500"> · {Math.round(rep.attendance_rate)}% show-up</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
                {/* Empty seats */}
                {Array.from({ length: Math.max(0, (game.max_players || 0) - (game.participants_count || 0)) }).map((_, i) => (
                  <div key={`empty-${i}`} className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-slate-800">
                    <div className="h-8 w-8 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center">
                      <Users className="h-3 w-3 text-slate-700" />
                    </div>
                    <div className="text-xs text-slate-600">Open spot</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Squad chat */}
            {isParticipant && <SquadChat gameId={gameId} userId={user?.id} />}
          </div>

          {/* Right: Action panel */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-2xl border border-cyan-500/20 p-5" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.07), var(--ax-card-bg-soft))' }} data-testid="arenax-game-action-panel">
              <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">YOUR PASS</div>
              <div className="space-y-2 mb-4 text-sm text-slate-300">
                <Row label="Entry fee" value={game.entry_fee_per_player > 0 ? `₹${game.entry_fee_per_player}` : 'Free'} />
                <Row label="Spots remaining" value={`${game.spots_remaining} of ${game.max_players}`} />
                <Row label="Status" value={badge.text} />
                <Row label="Host" value={(game.participants || []).find(p => p.role === 'host')?.player_name || '—'} />
              </div>
              {isHost ? (
                game.status_derived === 'cancelled' ? (
                  <div className="text-center text-xs text-slate-500 py-3">Game cancelled</div>
                ) : (
                  <button onClick={onCancel} data-testid="arenax-game-cancel"
                    className="w-full px-4 py-3 rounded-xl font-bold text-rose-300 text-sm border border-rose-500/30 hover:bg-rose-500/10 transition-colors">
                    Cancel this game
                  </button>
                )
              ) : isParticipant ? (
                <button onClick={onLeave} data-testid="arenax-game-leave"
                  className="w-full px-4 py-3 rounded-xl font-bold text-slate-400 text-sm border border-slate-700 hover:bg-slate-800 transition-colors">
                  Leave game
                </button>
              ) : game.is_full ? (
                <button disabled className="w-full px-4 py-3 rounded-xl font-bold text-slate-500 text-sm bg-slate-800/60 cursor-not-allowed">
                  Game is full
                </button>
              ) : ['cancelled', 'completed'].includes(game.status_derived) ? (
                <button disabled className="w-full px-4 py-3 rounded-xl font-bold text-slate-500 text-sm bg-slate-800/60 cursor-not-allowed capitalize">
                  {game.status_derived}
                </button>
              ) : (
                <button onClick={onJoin} disabled={joining} data-testid="arenax-game-join"
                  className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ background: 'var(--ax-primary-gradient)' }}>
                  {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Join Game {game.entry_fee_per_player > 0 && `· ₹${game.entry_fee_per_player}`} <ArrowRight className="h-4 w-4" /></>}
                </button>
              )}
              <div className="mt-3 text-[10px] text-slate-600 text-center">{game.entry_fee_per_player > 0 ? 'Secure payments via Razorpay · Free refunds if you leave before start' : 'Free to join · Just show up'}</div>
            </div>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-200">{value}</span>
    </div>
  );
}

// Polling-based squad chat (every 5 seconds).
function SquadChat({ gameId, userId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const sinceRef = useRef(null);
  const scrollRef = useRef(null);

  const load = async () => {
    const q = sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : '';
    try {
      const r = await api.get(`/arenax/games/${gameId}/messages${q}`);
      const fresh = r.data.messages || [];
      if (fresh.length) {
        setMsgs(prev => sinceRef.current ? [...prev, ...fresh] : fresh);
        sinceRef.current = fresh[fresh.length - 1].created_at;
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/arenax/games/${gameId}/messages`, { text: t });
      setText('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-cyan-500/20 p-5" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.06), var(--ax-card-bg-soft))' }} data-testid="arenax-squad-chat">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-4 w-4 text-cyan-300" />
        <h3 className="text-sm font-bold text-slate-100">Squad Chat</h3>
        <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1.5">
          <span className="relative h-1.5 w-1.5"><span className="absolute inset-0 rounded-full bg-emerald-400" /><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" /></span>
          LIVE
        </span>
      </div>
      <div ref={scrollRef} className="h-80 overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-12">No messages yet. Break the ice 🏏</div>
        ) : msgs.map(m => {
          const isSelf = m.player_id === userId;
          const isSystem = m.kind === 'system';
          if (isSystem) {
            return (
              <div key={m.id} className="text-center text-[10px] text-slate-500 italic tracking-wide py-1">
                {m.text}
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-xl px-3 py-2 ${
                isSelf ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-100'
              }`}>
                {!isSelf && <div className="text-[10px] font-bold text-cyan-300 mb-0.5">{m.player_name}</div>}
                <div className="text-sm break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Type a message…"
          data-testid="arenax-squad-chat-input"
          className="flex-1 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500 focus:outline-none text-sm text-slate-100 placeholder:text-slate-600" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="arenax-squad-chat-send"
          className="px-4 rounded-xl font-bold text-slate-950 transition-all disabled:opacity-40"
          style={{ background: 'var(--ax-primary-gradient)' }}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
